import React from 'react'
import clsx from 'clsx'
import debounce from 'lodash.debounce'
import { useAppDispatch, useAppSelector } from '@SonarCX/hooks/redux'
import { Popper, useTheme } from '@mui/material'
import {
  ArrowDown2,
  ExportSquare,
  TagUser,
  Calendar as CalendarIcon,
  Rank,
  Flag2,
  Hierarchy3
} from 'iconsax-react'
import { AddErrorAlert } from '@SonarCX/slices/alert'

import Tooltip from '../../atoms/Tooltip'
import Spacer from '../../atoms/Spacer'
import TaskModal from '../ChatModals/TaskModal'
import MediaAvatar from '../../atoms/MediaAvatar'
import Calendar from '../../atoms/Calendar'
import TaskAssignee from '../../atoms/TaskAssignee'
import AddAsigneeButton from './AddAsigneeButton'
import DropdownV2 from '../../atoms/DropdownV2'
import DownArrowSmallIcon from '../../icons/DownArrowSmallIcon'
import CloseAddAssigneeButton from './CloseAddAssigneeButton'
import AsigneeRow from './AssigneeRow'
import Comments from '@SonarCX/pages/Tasks/Cells/Title/Comments'
import Description from '@SonarCX/pages/Tasks/Cells/Title/Description'

import { formatDateRelative } from '@SonarCX/utilities'
import { useAgent } from '@SonarCX/providers/AgentProvider'
import { useTaskAPI } from '@SonarCX/providers/TaskAPIProvider'
import { useTaskComments } from '@SonarCX/providers/TaskCommentsProvider'
import { useUserAPI } from '@SonarCX/providers/UserAPIProvider'
import { useTaskList } from '@SonarCX/providers/TaskListProvider'
import { useOwner } from '@SonarCX/providers/OwnerProvider'
import { fetchStatus, selectTaskStatus } from '@SonarCX/slices/taskStatus'
import { fetchPriorities, selectTaskPriority } from '@SonarCX/slices/priority'
import { formatTimeSince } from '@SonarCX/timeUtilities'
import useTaskPermissions from '@SonarCX/hooks/permissions/useTaskPermissions'
import useDelayedUserSearch from '@SonarCX/hooks/useDelayedUserSearch'
import useStyles from './styles'

const TimelineTaskV2 = ({ task, isPeer = false }) => {
  const theme = useTheme()
  const dispatch = useAppDispatch()

  const {
    statuses,
    loaded: taskStatusLoaded,
    loading: taskStatusLoading
  } = useAppSelector(selectTaskStatus)
  const {
    priorities,
    loaded: taskPriorityLoaded,
    loading: taskPriorityLoading
  } = useAppSelector(selectTaskPriority)

  const { currentUser } = useAgent()
  const { tasks, updateTask } = useTaskAPI()
  const { allUsers, loadUser } = useUserAPI()
  const { setIDs, reset: resetTasks } = useTaskList()
  const { tasks: ownerTasks } = useOwner()
  const { search, results } = useDelayedUserSearch(false)
  const {
    canUpdate,
    canEdit,
    canDelete,
    canClose,
    canView: canViewTask
  } = useTaskPermissions(task)

  const {
    created_at,
    created_by,
    details,
    due_date,
    id,
    media_ids,
    session_ids,
    status_id,
    category_id,
    tags,
    title,
    type_id,
    watcher_ids,
    priority,
    concerning_user_id,
    assignee_ids,
    owner_id
  } = task

  let { comments: commentsNumber } = task

  if (!commentsNumber) {
    commentsNumber = 0
  }

  const [values, setValues] = React.useState({
    status: status_id,
    type: type_id,
    primary: assignee_ids ?? [],
    watchers: watcher_ids,
    title,
    content: details,
    file: null,
    due_date,
    priority: priority?.id,
    category: category_id,
    created_at,
    client: concerning_user_id,
    tags: [],
    media_ids,
    temp: assignee_ids ?? [],
    owner_id: owner_id ?? null
  })

  const { counts } = useTaskComments()

  const [searchValue, setSearchValue] = React.useState('')
  const [seeAllAgents, setSeeAllAgents] = React.useState(false)
  const [taskModalOpen, setTaskModalOpen] = React.useState(false)
  const [creator, setCreator] = React.useState(null)
  const [subtasks, setSubtasks] = React.useState([])
  const [editSubtasks, setEditSubtasks] = React.useState(false)
  const [taskOpen, setTaskOpen] = React.useState(null)
  const [isOpen, setIsOpen] = React.useState(false)
  const [showCommentPreview, setShowCommentPreview] = React.useState(false)
  const [shouldCheckClose, setShouldCheckClose] = React.useState(false)

  const targetRef = React.useRef()
  const calenderRef = React.useRef(null)
  const anchorEl = React.useRef(null)
  const inputRef = React.useRef(null)

  const comments = React.useMemo(() => {
    return Math.max(counts[task.id] ?? 0, task?.comments ?? 0)
  }, [counts, task.id, task.comments])

  const handleToggleClick = React.useCallback(() => {
    setIsOpen(old => !old)
  }, [])

  const formattedTime = React.useMemo(() => {
    try {
      return formatTimeSince(new Date(created_at), Date.now(), true)
    } catch {
      console.warn('Invalid date for Message', created_at)
      return formatTimeSince(Date.now(), Date.now(), true)
    }
  }, [created_at])

  // Sets / loads creator
  React.useEffect(() => {
    const creator = created_by || concerning_user_id
    const user = allUsers[creator]

    if (user) {
      setCreator(user)
    } else {
      loadUser(creator)
    }
  }, [allUsers, created_by, concerning_user_id, loadUser])

  const classes = useStyles({ canEdit, expanded: isOpen })

  React.useEffect(() => {
    if (!taskStatusLoaded && !taskStatusLoading) {
      dispatch(fetchStatus())
    }
  }, [taskStatusLoaded, taskStatusLoading, dispatch])

  React.useEffect(() => {
    if (!taskPriorityLoaded && !taskPriorityLoading) {
      dispatch(fetchPriorities())
    }
  }, [taskPriorityLoaded, taskPriorityLoading, dispatch])

  const [savedContent, setSavedContent] = React.useState('')

  const openModal = React.useCallback(() => {
    setIDs(ownerTasks, taskOpen ?? id)
    setTaskModalOpen(true)
  }, [setIDs, ownerTasks, taskOpen, id])

  const closeModal = React.useCallback(() => {
    resetTasks()
    setTaskModalOpen(false)
    setTaskOpen(null)
    setSubtasks(null)
  }, [resetTasks])

  let delayUploadType
  const handleUpload = React.useCallback(
    async (data, isFormatted) => {
      setSavedContent(data)

      const uploadId = data.id ?? id

      try {
        let toUpload = { ...data }

        // timeline task direct updates dont have access to parent id, need to fetch
        const pendingUpdateTask = tasks[uploadId]

        // Fix formatting on fields to match api
        if (!isFormatted) {
          const {
            primary,
            content,
            due_date,
            status,
            type,
            watchers,
            priority,
            category,
            client
          } = toUpload

          const trimmedDate = new Date(due_date).toISOString()

          toUpload = {
            ...toUpload,
            assignee_ids: primary,
            details: content,
            due_date: trimmedDate,
            primary_assignee_id: primary[0] ?? null,
            status_id: status,
            tags,
            type_id: type,
            watcher_ids: watchers,
            priority_id: priority,
            category_id: category ?? tasks[id]?.category_id ?? null,
            concerning_user_id: client || tasks[id]?.concerning_user_id || null,
            owner_id: owner_id ?? null
          }

          delete toUpload.primary
          delete toUpload.content
          delete toUpload.date
          delete toUpload.status
          delete toUpload.priority
          delete toUpload.type
          delete toUpload.watchers
          delete toUpload.created_at
        }

        delete toUpload.id

        toUpload = {
          ...toUpload,
          media_ids: data.media_ids ?? [],
          parent_id: pendingUpdateTask.parent_id ?? null,
          session_ids,
          comments
        }

        if (JSON.stringify(data) !== JSON.stringify(savedContent)) {
          delayUploadType.cancel()
          await updateTask(uploadId, toUpload, commentsNumber)
          setSubtasks(null)
        }
      } catch (err) {
        // error happened, reset content
        setSavedContent('')
        console.error('File Upload failed in TimelineTask', err)
        dispatch(AddErrorAlert({ err }))
      }
    },
    [
      id,
      tasks,
      session_ids,
      comments,
      savedContent,
      tags,
      owner_id,
      delayUploadType,
      updateTask,
      commentsNumber,
      dispatch
    ]
  )

  // differentiate between here and on blur, set content accordingly
  // eslint-disable-next-line
  delayUploadType = React.useCallback(
    debounce(data => handleUpload(data, false), 2000),
    [handleUpload]
  )

  React.useEffect(() => {
    const newVals = {
      content: task.details,
      due_date: task.due_date,
      primary: task.assignee_ids ?? [],
      assignee_ids: task.assignee_ids,
      priority: task.priority_id,
      status: task.status_id,
      title: task?.title,
      type: task.type_id,
      watchers: task.watcher_ids,
      created_at: created_at,
      client: task.concerning_user_id,
      section: task.category_id,
      temp: task.assignee_ids
    }

    setValues(newVals)

    // eslint-disable-next-line
  }, [task])

  const handleChange = prop => e => {
    let val
    if (prop === 'due_date') {
      val = e.toDate()
    } else {
      val = e.target.value
    }

    setValues(vals => {
      const newVals = { ...vals, [prop]: val }
      if (['status', 'priority', 'due_date', 'assignee_ids'].includes(prop)) {
        handleUpload(newVals, false)
      }
      return newVals
    })
  }

  const handleRemoveAsignee = React.useCallback(
    id => {
      const newAsignees = [...values.primary].filter(p => p !== id)
      setValues(vals => {
        const newVals = { ...vals, primary: newAsignees }
        handleUpload(newVals, false)
        return newVals
      })
    },
    [values, handleUpload]
  )

  const handleAddAsignee = React.useCallback(
    id => {
      const newAsignees = Array.from(new Set([...values.primary, id]))
      setValues(vals => {
        const newVals = { ...vals, primary: newAsignees }
        handleUpload(newVals, false)
        return newVals
      })
    },
    [values, handleUpload]
  )

  const onSearchChange = React.useCallback(
    value => {
      setSearchValue(value)
      search(value)
    },
    [search]
  )

  const handleOnTaskCreated = async data => {
    await handleUpload(data, true)
  }

  const numberOfAssignees = values?.primary?.length || 0
  //show max 3 asignees
  const asigneesToRender = [...values.primary].slice(0, 3)

  return (
    <div className={classes.root} ref={targetRef}>
      {!isPeer && (
        <div className={classes.topRow}>
          <Spacer />

          <Tooltip
            title={<div>Created {formattedTime}</div>}
            placement='bottom'
            arrow
          >
            <div className={classes.topRoot}>
              <div className='creator'>
                {creator?.id === currentUser()?.id
                  ? 'You'
                  : `${creator?.name || ''}`}
              </div>

              <div className='time'>
                created a task &middot; {formattedTime}
              </div>
            </div>
          </Tooltip>

          <MediaAvatar user={creator} className={classes.avatar} />
        </div>
      )}

      <div className={classes.bottomRow}>
        <Spacer />

        <div className={classes.taskRoot}>
          <div className={classes.header}>
            <div className={classes.titleSection}>
              <div className={classes.title}>
                {task.parent_id ? (
                  <Tooltip title='Subtask' arrow>
                    <Hierarchy3
                      size={theme.size.sm}
                      className={classes.noShrink}
                      style={{ marginRight: '0.5rem' }}
                    />
                  </Tooltip>
                ) : task.sub_tasks > 0 ? (
                  <Tooltip title='Parent' arrow>
                    <Rank
                      size={theme.size.sm}
                      className={classes.noShrink}
                      style={{ marginRight: '0.5rem' }}
                    />
                  </Tooltip>
                ) : null}
                {title}
                {isOpen && (
                  <ExportSquare
                    size={theme.size.sm}
                    onClick={openModal}
                    className={classes.noShrink}
                  />
                )}
              </div>
            </div>

            <div className={classes.expandCollapseSection}>
              <ArrowDown2
                size={theme.size.base}
                onClick={handleToggleClick}
                className={classes.noShrink}
              />
            </div>
          </div>

          <div aria-expanded={!isOpen} className={classes.contentArea}>
            <div className={classes.infoArea}>
              <Comments
                task={task}
                showCommentPreview={showCommentPreview}
                setShowCommentPreview={setShowCommentPreview}
                shouldCheckClose={shouldCheckClose}
                setShouldCheckClose={setShouldCheckClose}
              />

              <Description task={task} />
            </div>

            <div className={classes.dataArea}>
              <div className={classes.contentAreaRow}>
                <span
                  className={clsx(
                    classes.contentAreaLabel,
                    numberOfAssignees > 0 && classes.baseLine
                  )}
                >
                  <TagUser size='13' variant='Bold' color='grey' />
                  <label className={classes.contentLabel}>Assigned to</label>
                </span>

                <div>
                  <span className={classes.asignedToRoot}>
                    {seeAllAgents ? (
                      <div>
                        {values.primary.map(person => (
                          <AsigneeRow
                            key={person}
                            userId={person}
                            canEdit={canEdit}
                            handleRemoveAsignee={handleRemoveAsignee}
                            classes={classes}
                          />
                        ))}

                        {canEdit && (
                          <div
                            className={classes.addAssigneContainer}
                            ref={anchorEl}
                          >
                            <input
                              ref={inputRef}
                              value={searchValue}
                              placeholder='Type to search'
                              className={classes.asigneeSearch}
                              onChange={e => {
                                onSearchChange(e.target.value)
                              }}
                            />

                            <CloseAddAssigneeButton
                              className={classes.asigneeSearchCloseBtn}
                              onClick={() => {
                                setSeeAllAgents(false)
                                search('')
                                setSearchValue('')
                              }}
                            />
                          </div>
                        )}
                      </div>
                    ) : (
                      <React.Fragment>
                        {asigneesToRender.map((person, index) => (
                          <TaskAssignee
                            key={person}
                            userId={person}
                            index={index}
                            numberOfAssignees={numberOfAssignees}
                            hideAnimation
                          />
                        ))}

                        {numberOfAssignees > 3 && (
                          <span className={classes.additionalAsignees}>{`${
                            numberOfAssignees - 3
                          }+`}</span>
                        )}

                        {canEdit && !seeAllAgents && (
                          <AddAsigneeButton
                            className={classes.addAsigneeBtn}
                            onClick={() => setSeeAllAgents(true)}
                          />
                        )}
                      </React.Fragment>
                    )}
                  </span>

                  {numberOfAssignees > 0 && (
                    <div
                      className={classes.seeDetails}
                      onClick={() => {
                        setSeeAllAgents(!seeAllAgents)
                      }}
                    >
                      {seeAllAgents ? 'Show Less' : 'Show More'}
                    </div>
                  )}
                </div>
              </div>

              <div className={classes.contentAreaRow}>
                <span className={classes.contentAreaLabel}>
                  <CalendarIcon size='13' variant='Bold' color='grey' />

                  <label className={classes.contentLabel}>Due Date</label>
                </span>

                <span ref={calenderRef}>
                  <Calendar
                    value={values.due_date}
                    onChange={handleChange('due_date')}
                    anchorEl={calenderRef}
                    disabled={!canEdit}
                  >
                    <div className={classes.daySoon}>
                      <label>{formatDateRelative(values.due_date, true)}</label>

                      <DownArrowSmallIcon style={{ flexShrink: 0 }} />
                    </div>
                  </Calendar>
                </span>
              </div>

              <div className={classes.contentAreaRow}>
                <span className={classes.contentAreaLabel}>
                  <Rank size='13' variant='Bold' color='grey' />

                  <label className={classes.contentLabel}>Priority</label>
                </span>

                <span className={classes.dropdown}>
                  <DropdownV2
                    options={priorities}
                    disabled={!canEdit}
                    value={priorities.find(
                      priority => priority.id === values.priority
                    )}
                    onSelect={val =>
                      handleChange('priority')({ target: { value: val.id } })
                    }
                    renderOption={priorities => priorities.name}
                  />
                </span>
              </div>

              <div className={classes.contentAreaRow}>
                <span className={classes.contentAreaLabel}>
                  <Flag2 size='13' variant='Bold' color='grey' />

                  <label className={classes.contentLabel}>Status</label>
                </span>

                <span className={classes.dropdown}>
                  <DropdownV2
                    disabled={!canEdit}
                    options={statuses}
                    value={statuses.find(status => status.id === values.status)}
                    onSelect={val =>
                      handleChange('status')({ target: { value: val.id } })
                    }
                    renderOption={statuses => statuses.name}
                  />
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {taskModalOpen && (
        <TaskModal
          open={taskModalOpen}
          onClose={closeModal}
          onTaskCreated={handleOnTaskCreated}
          superVals={values}
          updateSuperVals={setValues}
          taskId={taskOpen ?? id}
          subtasks={subtasks}
          editSubtasks={editSubtasks}
          setEditSubtasks={setEditSubtasks}
          disableTaskCreation
          setTaskOpen={setTaskOpen}
          canEdit={canEdit}
          canUpdate={canUpdate}
          canDelete={canDelete}
          canViewTask={canViewTask}
          canClose={canClose}
        />
      )}

      {searchValue !== '' && results.length > 0 && (
        <Popper
          open={true}
          disablePortal={false}
          anchorEl={anchorEl?.current}
          style={{ zIndex: '1500' }}
          placement='bottom-end'
        >
          <div className={classes.menu}>
            <span className={classes.menuLabel}>Results</span>

            <div className={classes.items}>
              {results.map(opt => (
                <div
                  key={opt.id}
                  className={classes.item}
                  onClick={() => {
                    handleAddAsignee(opt.id)
                    setSearchValue('')
                    search('')
                    inputRef.current?.focus()
                  }}
                >
                  <MediaAvatar user={opt} />

                  <label>{opt?.name}</label>
                </div>
              ))}
            </div>
          </div>
        </Popper>
      )}
    </div>
  )
}

export default TimelineTaskV2
